import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { createLotusCareStateApi } from '@/shared/api/lotus';
import { analytics, endWorkspaceSession, startWorkspaceSession } from '@/shared/lib';

import {
  createCareStateClient,
  type CareStateStatus,
  type CareStateUpdate,
  useCareState,
} from '@/entities/care-state-engine';

import type { CareStateDomainDb, ClinicalScopeT } from '../model/types';
import { selectScopedCareState } from '../selectors/selectScopedCareState';

function scopeId(scope: ClinicalScopeT): string | undefined {
  return scope.type === 'patient' ? undefined : scope.id;
}

export type CareStateDomainContextValue = {
  patientUserId: string;
  careState: CareStateDomainDb | null;
  scopedCareState: CareStateDomainDb | null;
  scope: ClinicalScopeT;
  setScope(next: ClinicalScopeT): void;
  status: CareStateStatus;
  lastError: string | null;
  debug: {
    serverCursor: number | null;
    pendingUpdateCount: number;
    pendingUnsubmittedCount: number;
    pendingSubmittedCount: number;
  };
  update(update: CareStateUpdate<CareStateDomainDb>): void;
};

const CareStateDomainContext = createContext<CareStateDomainContextValue | null>(null);

export type CareStateProviderProps = {
  patientUserId: string;
  children: React.ReactNode;
};

/**
 * CareStateProvider
 *
 * Owns a single CareState polling lifecycle for a patient workspace.
 * Exposes both raw CareState and a derived scoped view based on ClinicalScope.
 */
export function CareStateProvider({ patientUserId, children }: CareStateProviderProps) {
  const api = useMemo(() => createLotusCareStateApi(), []);
  const client = useMemo(
    () =>
      createCareStateClient<CareStateDomainDb>({
        userId: patientUserId,
        api,
      }),
    [api, patientUserId]
  );

  const {
    state: careState,
    update,
    status,
    lastError,
    debug,
  } = useCareState<CareStateDomainDb>(client);

  const [scope, setScope] = useState<ClinicalScopeT>({ type: 'patient' });
  const previousScopeRef = useRef<ClinicalScopeT>({ type: 'patient' });

  useEffect(() => {
    if (patientUserId.trim() === '') {
      return;
    }

    startWorkspaceSession(patientUserId);
    return () => {
      endWorkspaceSession(patientUserId);
    };
  }, [patientUserId]);

  useEffect(() => {
    const previous = previousScopeRef.current;
    const hasChanged =
      previous.type !== scope.type ||
      (previous.type !== 'patient' && previous.id !== scopeId(scope));

    if (!hasChanged) {
      return;
    }

    previousScopeRef.current = scope;
    void analytics.track({
      name: 'carestate.scope.changed',
      context: {
        actor: 'clinician',
        patientUserId,
        scope,
      },
      properties: {
        fromScopeType: previous.type,
        fromScopeId: scopeId(previous),
        toScopeType: scope.type,
        toScopeId: scopeId(scope),
      },
    });
  }, [patientUserId, scope]);

  // Auto-focus the first open request when care state first loads.
  const hasAutoFocusedRef = useRef(false);
  useEffect(() => {
    if (careState === null || hasAutoFocusedRef.current) return;
    // Only auto-focus if scope hasn't been manually changed from the initial default.
    if (scope.type !== 'patient') return;

    hasAutoFocusedRef.current = true;

    const firstOpen = careState.requests.find((r) => r.status === 'Open');
    if (firstOpen !== undefined) {
      setScope({ type: 'request', id: firstOpen.id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [careState]);

  // Reset scope if the current scoped entity no longer exists
  useEffect(() => {
    if (careState === null) {
      return;
    }

    if (scope.type === 'problem') {
      const exists = careState.problems.some((p) => p.id === scope.id);
      if (!exists) {
        setScope({ type: 'patient' });
      }
      return;
    }

    if (scope.type === 'request') {
      const exists = careState.requests.some((r) => r.id === scope.id);
      if (!exists) {
        setScope({ type: 'patient' });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [careState, scope.type, scope.type === 'patient' ? null : scope.id]);

  const scopedCareState = useMemo(
    () => (careState === null ? null : selectScopedCareState(careState, scope)),
    [careState, scope]
  );

  const value: CareStateDomainContextValue = useMemo(
    () => ({
      patientUserId,
      careState,
      scopedCareState,
      scope,
      setScope,
      status,
      lastError,
      debug,
      update,
    }),
    [patientUserId, careState, scopedCareState, scope, status, lastError, debug, update]
  );

  return (
    <CareStateDomainContext.Provider value={value}>{children}</CareStateDomainContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useCareStateDomain(): CareStateDomainContextValue {
  const ctx = useContext(CareStateDomainContext);
  if (ctx === null) {
    throw new Error('useCareStateDomain must be used within a CareStateProvider');
  }
  return ctx;
}
